/**
 * Single source of truth for per-route SEO metadata.
 *
 * Consumed by:
 *   1. Runtime: src/seo/Seo.jsx (react-helmet-async) — keeps <head> in sync
 *      as the user navigates client-side.
 *   2. Build time: scripts/build-seo.js — writes per-route static index.html
 *      files so social/search crawlers (WhatsApp, LinkedIn, Slack, X, Facebook,
 *      Googlebot without JS) get route-specific meta in the initial payload.
 *   3. Build time: scripts/build-sitemap.js — emits build/sitemap.xml.
 *
 * IMPORTANT: canonical always points to the production origin
 * (https://thoratech.global) so preview and production consolidate correctly.
 */

export const SITE_ORIGIN = "https://thoratech.global";
export const SITE_NAME = "Thoratech";

// -- Structured data blocks (P0-B) ------------------------------------------
// Location is intentionally kept at Pune, Maharashtra, IN — the new Sprint
// Antaya office becomes operational from 10 September; street address, postal
// code and geo will be added at that point, not before.

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  name: "Thoratech Innovations",
  legalName: "Thoratech Innovations Private Limited",
  alternateName: "Thoratech",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/brand/TT_Logo_TrueTransparent.svg`,
  image: `${SITE_ORIGIN}/og/og-home.jpg`,
  description:
    "Thoratech is a CX and BPO managed operations partner for manufacturers and OEMs. We manage customer support, lead handling, dealer coordination, service follow-up, warranty, AMC and operational reporting from Pune, India.",
  founder: {
    "@type": "Person",
    name: "Harshal Thorat",
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Pune",
    addressRegion: "Maharashtra",
    addressCountry: "IN",
  },
  areaServed: [
    { "@type": "Country", name: "India" },
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-84858-34674",
    email: "hello@thoratech.global",
    contactType: "customer service",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi", "Marathi"],
  },
  sameAs: [
    "https://www.linkedin.com/company/thoratech-global/",
    "https://maps.app.goo.gl/BWJViJMEGtDWmKoH9",
  ],
  knowsAbout: [
    "Customer Experience Outsourcing",
    "Business Process Outsourcing",
    "Outsourced Customer Support",
    "Managed Customer Operations",
    "Lead Handling and Qualification",
    "Dealer Channel Coordination",
    "Service Follow-up and Complaint Governance",
    "Warranty and AMC Operations",
    "Operational Reporting for Manufacturers and OEMs",
  ],
  // progressKODE is a Thoratech-owned public brand / engineering arm, not a
  // separately incorporated legal entity. Modelled as a schema.org Brand.
  brand: {
    "@type": "Brand",
    name: "progressKODE",
    url: "https://progresskode.com",
  },
};

const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_ORIGIN}${it.path}`,
  })),
});

// -- Per-route metadata ------------------------------------------------------

export const ROUTES = {
  "/": {
    path: "/",
    title: "BPO & Customer Operations for OEMs | Thoratech Pune",
    description:
      "Thoratech provides outsourced customer support, lead handling, dealer coordination, service follow-up, warranty, AMC and reporting for OEMs from Pune.",
    canonical: `${SITE_ORIGIN}/`,
    og: {
      title: "BPO & Customer Operations for OEMs | Thoratech Pune",
      description:
        "Thoratech provides outsourced customer support, lead handling, dealer coordination, service follow-up, warranty, AMC and reporting for OEMs from Pune.",
      image: `${SITE_ORIGIN}/og/og-home.jpg`,
      type: "website",
    },
    twitter: { card: "summary_large_image" },
    jsonLd: [ORGANIZATION_JSONLD],
    sitemap: { priority: "1.0", changefreq: "weekly" },
  },

  "/careers": {
    path: "/careers",
    title: "BPO Jobs in Balewadi, Pune | Thoratech Careers",
    description:
      "Explore customer care, team leader, quality, business development and HR opportunities at Thoratech in Balewadi, Pune. Work from office, 6 days.",
    canonical: `${SITE_ORIGIN}/careers`,
    og: {
      title: "BPO Jobs in Balewadi, Pune | Thoratech Careers",
      description:
        "Explore customer care, team leader, quality, business development and HR opportunities at Thoratech in Balewadi, Pune. Work from office, 6 days.",
      image: `${SITE_ORIGIN}/og/og-careers.jpg`,
      type: "website",
    },
    twitter: { card: "summary_large_image" },
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Careers", path: "/careers" },
      ]),
    ],
    sitemap: { priority: "0.9", changefreq: "weekly" },
  },

  "/blog": {
    path: "/blog",
    title: "Field Journal: CX, BPO & Customer Operations Notes | Thoratech",
    description:
      "Operating notes on running customer operations for manufacturers and dealer networks. Lead lifecycle, complaint governance, AMC and dealer operations.",
    canonical: `${SITE_ORIGIN}/blog`,
    og: {
      title: "Thoratech Field Journal: notes from the operations floor",
      description:
        "Operating notes on running customer operations for manufacturers and dealer networks. Lead lifecycle, complaint governance, AMC and dealer operations.",
      image: `${SITE_ORIGIN}/og/og-blog.jpg`,
      type: "website",
    },
    twitter: { card: "summary_large_image" },
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Field journal", path: "/blog" },
      ]),
    ],
    sitemap: { priority: "0.7", changefreq: "weekly" },
  },

  "/privacy": {
    path: "/privacy",
    title: "Privacy Policy | Thoratech (DPDP & GDPR)",
    description:
      "How Thoratech collects, uses and protects data from website visitors and job applicants, aligned with India's DPDP Act, 2023 and the EU GDPR.",
    canonical: `${SITE_ORIGIN}/privacy`,
    og: {
      title: "Privacy Policy | Thoratech",
      description:
        "How Thoratech collects, uses and protects data from website visitors and job applicants, aligned with India's DPDP Act, 2023 and the EU GDPR.",
      image: `${SITE_ORIGIN}/og/og-home.jpg`,
      type: "website",
    },
    twitter: { card: "summary_large_image" },
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Privacy", path: "/privacy" },
      ]),
    ],
    sitemap: { priority: "0.3", changefreq: "yearly" },
  },
};

const MONTHS = ["january","february","march","april","may","june","july","august","september","october","november","december"];

/** "8 September 2026" | "December 2025" -> "2026-09-08" | "2025-12-01". */
export function toIsoDate(d) {
  const m = String(d || "").trim().match(/^(?:(\d{1,2})\s+)?([A-Za-z]+)\s+(\d{4})$/);
  if (!m) return null;
  const month = MONTHS.indexOf(m[2].toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, "0")}-${String(m[1] || 1).padStart(2, "0")}`;
}

const blogPosting = (p, url, image, description) => {
  const iso = toIsoDate(p.date);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: p.title,
    description,
    image: [image],
    ...(iso ? { datePublished: iso, dateModified: iso } : {}),
    author: {
      "@type": "Person",
      name: "Harshal Thorat",
      url: "https://www.linkedin.com/in/harshal-thorat/",
    },
    publisher: {
      "@type": "Organization",
      name: "Thoratech Innovations Private Limited",
      url: SITE_ORIGIN,
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/brand/TT_Logo_TrueTransparent.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
};

/** Expand /blog/:slug into one entry per post using the blog content module. */
export function expandBlogRoutes(blogPosts) {
  return blogPosts.map((p) => {
    const url = `${SITE_ORIGIN}/blog/${p.slug}`;
    const image = p.hero && p.hero.startsWith("http") ? p.hero : `${SITE_ORIGIN}${p.hero || "/og/og-blog.jpg"}`;
    const title = p.title.length > 60 ? p.title.slice(0, 57) + "..." : p.title;
    const description =
      p.seoDescription ||
      ((p.summary || "").length > 155 ? p.summary.slice(0, 152) + "..." : p.summary);
    return {
      path: `/blog/${p.slug}`,
      title: p.seoTitle || `${p.title} | Thoratech`,
      description,
      canonical: url,
      og: {
        title: p.seoTitle ? p.seoTitle.replace(/\s*\|\s*Thoratech$/, "") : title,
        description,
        image,
        type: "article",
      },
      twitter: { card: "summary_large_image" },
      jsonLd: [
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Field journal", path: "/blog" },
          { name: p.title, path: `/blog/${p.slug}` },
        ]),
        blogPosting(p, url, image, description),
      ],
      sitemap: { priority: "0.6", changefreq: "monthly", lastmod: p.date || null },
    };
  });
}

/** Read on both server (build script) and client (Seo.jsx). */
export default ROUTES;
